import { useEffect, useState } from "react";
import { AutopilotStep, DotState } from "./types";
import { useTypingText } from "hooks/useTypingText/";
import { useStepTimer } from "hooks/useStepTimer/";
import { useTranslate } from "@refinedev/core";
import { jitter } from "utils/jitter/";
import {
  ChevronBtn,
  DescriptionText,
  DialogBox,
  DialogTop,
  Skeleton,
  slideUp,
  StepContent,
  StepMeta,
  StepRow,
  StepSpacer,
  StepTitle,
  TimelineCol,
  TimelineDot,
  TimerBadge,
} from "./Autopilot.styled";
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { FinalVisual } from "./FinalVisual";
import styled from "styled-components";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import type { Templates } from "pages/media/components/storyboardEditor/types/";
import { useBrandKit } from "hooks/useBrandKit/";
import { radius } from "@libertify/ui";

interface FinalStepProps {
  step: AutopilotStep;
  index: number;
  isActive: boolean;
  isDone: boolean;
  onComplete: () => void;
  sections: SectionWithSlides[];
  templates: Templates;
  brandKitData: ReturnType<typeof useBrandKit>;
  mediaAspectRatio: number;
  isPublished?: boolean;
  skipAnimations?: boolean;
  stepRef?: (el: HTMLDivElement | null) => void;
}

export const FinalStep: React.FC<FinalStepProps> = ({
  step,
  index,
  isActive,
  isDone,
  onComplete,
  sections,
  templates,
  brandKitData,
  mediaAspectRatio,
  isPublished,
  skipAnimations,
  stepRef,
}) => {
  const t = useTranslate();
  const [expanded, setExpanded] = useState(false);
  const [showDialog, setDialogRevealed] = useState(isDone || skipAnimations);
  const [showVisual, setVisualRevealed] = useState(isDone || skipAnimations);
  const [countdown, setCountdown] = useState<number | null>(
    skipAnimations ? 3 : null
  );

  const label = t(step.labelKey);
  const shortDesc = t(step.shortDescKey);

  const typedShortDesc = useTypingText(shortDesc, showDialog && !isDone);
  const displayedShortDesc = isDone ? shortDesc : typedShortDesc;
  const timerLabel = useStepTimer(isActive, isDone);
  const dotState: DotState = isDone ? "done" : isActive ? "active" : "pending";

  useEffect(() => {
    if (!isActive) return;
    const t1 = setTimeout(() => setDialogRevealed(true), 500);
    const t2 = setTimeout(() => setVisualRevealed(true), 850);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    const delay = isPublished ? 0 : jitter(step.delayMs);
    const t = setTimeout(() => setCountdown(5), delay);
    return () => clearTimeout(t);
  }, [isActive, isPublished]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      onComplete();
      return;
    }
    const t = setTimeout(() => setCountdown((c) => (c ?? 1) - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onComplete]);

  return (
    <>
      <StepRow ref={stepRef}>
        <TimelineCol>
          <TimelineDot $state={dotState} />
        </TimelineCol>

        <StepContent>
          <StepMeta>
            {t("media.autopilot.stepPrefix")}{" "}
            {String(index + 1).padStart(2, "0")}
          </StepMeta>
          <StepTitle>{label}</StepTitle>

          {isActive && !showDialog && <Skeleton />}

          {showDialog && (
            <DialogBox>
              <DialogTop
                $clickable={isDone}
                $active={isActive && !isDone}
                onClick={() => isDone && setExpanded((e) => !e)}
              >
                <DescriptionText>{displayedShortDesc}</DescriptionText>
                {(isActive || isDone) && (
                  <TimerBadge $done={isDone}>{timerLabel}</TimerBadge>
                )}
                {(isActive || isDone) && (
                  <ChevronBtn>
                    {isActive || expanded ? (
                      <CaretUpIcon size={15} />
                    ) : (
                      <CaretDownIcon size={15} />
                    )}
                  </ChevronBtn>
                )}
              </DialogTop>
            </DialogBox>
          )}

          {showVisual && (
            <FinalVisual
              isActive={isActive}
              delayMs={step.delayMs}
              isPublished={isPublished}
              sections={sections}
              templates={templates}
              brandKitData={brandKitData}
              mediaAspectRatio={mediaAspectRatio}
            />
          )}

          <StepSpacer />
        </StepContent>
      </StepRow>

      {countdown !== null && countdown > 0 && (
        <SuccessBanner>
          <BannerEmojiCircle>🎬</BannerEmojiCircle>
          <BannerTextCol>
            <BannerTitle>
              {t("media.autopilot.steps.final.bannerTitle")}
            </BannerTitle>
            <BannerSubtitle>
              {t("media.autopilot.steps.final.bannerSubtitle")}
            </BannerSubtitle>
          </BannerTextCol>
          <BannerCountdown>{countdown}</BannerCountdown>
        </SuccessBanner>
      )}
    </>
  );
};

const SuccessBanner = styled.div`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: ${radius.box}px;
  padding: 18px 28px;
  box-shadow: 0 12px 40px rgba(5, 150, 105, 0.4);
  min-width: 370px;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 1000;
  animation: ${slideUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
`;

const BannerEmojiCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: ${radius.circle};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const BannerTextCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BannerTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
`;

const BannerSubtitle = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
`;

const BannerCountdown = styled.div`
  width: 42px;
  height: 42px;
  border-radius: ${radius.circle};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
`;
